import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const STATUS_API_URL = 'https://status.todesktop.com/api/v1/summary';
const CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes

export type StatusLevel =
  | 'operational'
  | 'degraded_performance'
  | 'partial_outage'
  | 'full_outage'
  | 'maintenance';

export interface AffectedComponent {
  id: string;
  name: string;
  group_name?: string;
  current_status: string;
}

export interface Incident {
  id: string;
  name: string;
  status: string;
  url: string;
  last_update_at: string;
  last_update_message: string;
  current_worst_impact?: string;
  affected_components: AffectedComponent[];
}

export interface Maintenance {
  id: string;
  name: string;
  status: string;
  url: string;
  last_update_at: string;
  last_update_message: string;
  affected_components: AffectedComponent[];
  started_at?: string;
  scheduled_end_at?: string;
  starts_at?: string;
  ends_at?: string;
}

export interface StatusSummary {
  page_title: string;
  page_url: string;
  ongoing_incidents: Incident[];
  in_progress_maintenances: Maintenance[];
  scheduled_maintenances: Maintenance[];
}

export interface SystemStatus {
  level: StatusLevel;
  message: string;
  incidents: Incident[];
  maintenances: Maintenance[];
  isLoading: boolean;
  error: string | null;
}

// Global cache to share state across all hook instances
let cachedStatus: StatusSummary | null = null;
let lastFetchTime: number | null = null;
let fetchPromise: Promise<StatusSummary> | null = null;

function getWorstStatusLevel(summary: StatusSummary): StatusLevel {
  // Check for ongoing incidents first
  if (summary.ongoing_incidents.length > 0) {
    const worstImpact = summary.ongoing_incidents.reduce((worst, incident) => {
      const impact = incident.current_worst_impact || 'degraded_performance';
      const severity = getSeverity(impact);
      return severity > getSeverity(worst) ? impact : worst;
    }, 'degraded_performance' as string);
    return worstImpact as StatusLevel;
  }

  // Check for in-progress maintenances
  if (summary.in_progress_maintenances.length > 0) {
    return 'maintenance';
  }

  return 'operational';
}

function getSeverity(status: string): number {
  switch (status) {
    case 'full_outage':
      return 4;
    case 'partial_outage':
      return 3;
    case 'degraded_performance':
      return 2;
    case 'maintenance':
      return 1;
    case 'operational':
    default:
      return 0;
  }
}

function getStatusMessage(level: StatusLevel, _summary: StatusSummary): string {
  switch (level) {
    case 'full_outage':
      return 'Major outage';
    case 'partial_outage':
      return 'Partial outage';
    case 'degraded_performance':
      return 'Degraded performance';
    case 'maintenance':
      return 'Maintenance in progress';
    case 'operational':
    default:
      return 'All systems operational';
  }
}

async function fetchStatus(): Promise<StatusSummary> {
  const response = await fetch(STATUS_API_URL);
  if (!response.ok) {
    throw new Error(`Failed to fetch status: ${response.status}`);
  }
  return response.json();
}

export function useSystemStatus(): SystemStatus {
  const [status, setStatus] = useState<StatusSummary | null>(cachedStatus);
  const [isLoading, setIsLoading] = useState(!cachedStatus);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const mountedRef = useRef(true);

  const shouldFetch = useCallback(() => {
    if (!lastFetchTime) return true;
    const elapsed = Date.now() - lastFetchTime;
    return elapsed >= CACHE_DURATION_MS;
  }, []);

  const doFetch = useCallback(async () => {
    // If there's already a fetch in progress, wait for it
    if (fetchPromise) {
      try {
        const result = await fetchPromise;
        if (mountedRef.current) {
          setStatus(result);
          setIsLoading(false);
          setError(null);
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(
            err instanceof Error ? err.message : 'Failed to fetch status',
          );
          setIsLoading(false);
        }
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    fetchPromise = fetchStatus();

    try {
      const result = await fetchPromise;
      cachedStatus = result;
      lastFetchTime = Date.now();
      if (mountedRef.current) {
        setStatus(result);
        setIsLoading(false);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch status');
        setIsLoading(false);
      }
    } finally {
      fetchPromise = null;
    }
  }, []);

  // Fetch on initial mount
  useEffect(() => {
    mountedRef.current = true;
    if (shouldFetch()) {
      doFetch();
    }
    return () => {
      mountedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch on navigation if cache is stale
  useEffect(() => {
    if (shouldFetch()) {
      doFetch();
    }
  }, [location.pathname, shouldFetch, doFetch]);

  const level = status ? getWorstStatusLevel(status) : 'operational';
  const message = status
    ? getStatusMessage(level, status)
    : 'All systems operational';

  return {
    level,
    message,
    incidents: status?.ongoing_incidents || [],
    maintenances: status?.in_progress_maintenances || [],
    isLoading,
    error,
  };
}
